import LuxonAdapter from "@date-io/luxon";
import { Button, TextField } from "@material-ui/core";
import { ThemeProvider } from "@material-ui/core/styles";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import CloseIcon from "@material-ui/icons/Close";
import { MuiPickersUtilsProvider, KeyboardDatePicker, KeyboardTimePicker } from "@material-ui/pickers";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { DateTime } from "luxon";
import { useContext, useState, useEffect } from "react";

import {
    pickerStyles,
    useHeaderStyles,
} from "@/common/components/ArchivePage/VideoRequestHeader/VideoRequestHeaderStyles";
import { LiveUnitMetadataContext } from "@/common/contexts/LiveUnitMetadataContext";
import {
    setVideoSelectionMode,
    setTimelineStart,
    setRequestDuration,
    setRequestStartTime,
} from "@/store/archives/slice";
import { useDispatch, RootState, useSelector } from "@/store/index";
import { setHidden } from "@/store/liveUnitNav/slice";
import { ArchiveImagesNavMeta } from "@/utilities/types/archive";

import { VideoRequestModal } from "../VideoRequestModal/VideoRequestModal";
import classes from "./VideoRequestHeader.scss";


export const VideoRequestHeader: React.FC = () => {
    const gridAndAccordionWidth: number = useSelector((state: RootState) => state.archives.gridAndAccordionWidth);
    const timelineWidth: number = useSelector((state: RootState) => state.archives.timelineWidth);

    const unitMetadata = useContext<ArchiveImagesNavMeta>(LiveUnitMetadataContext);
    const requestStartTimeString: string = useSelector((state: RootState) => state.archives.requestStartTime);
    const requestStartTime: DateTime = DateTime.fromISO(requestStartTimeString, { zone: unitMetadata.timezone });
    const requestDuration: number = useSelector((state: RootState) => state.archives.requestDuration);
    const timelineStart: string = useSelector((state: RootState) => state.archives.timelineStart);

    const headerClasses = useHeaderStyles();
    const [modalOpen, setModalOpen] = useState(false);

    const [selectedDate, setSelectedDate] = useState<DateTime | null>(requestStartTime);
    const [selectedTime, setSelectedTime] = useState<DateTime | null>(requestStartTime);

    const maxDurationInMinutes = 30;

    useEffect(() => {
        setSelectedDate(requestStartTime);
        setSelectedTime(requestStartTime);
    }, [requestStartTimeString]);

    const handleClickListItem = () => {
        setModalOpen(true);
    };

    const handleClose = () => {
        setModalOpen(false);
    };

    const dispatch = useDispatch();

    const handleVideoSelectionClose = () => {
        dispatch(setVideoSelectionMode(false));
        dispatch(setHidden(false));
    };

    const updateRequestStartDate = (datepickerDate: DateTime) => {
        const newDate = datepickerDate.toISODate();
        const diff = requestStartTime.toSeconds() - DateTime.fromISO(timelineStart).toSeconds();

        if (selectedTime) {
            const timeLineStartValue = selectedTime
                .minus({ seconds: diff })
                .toISOTime({ includeOffset: true, includePrefix: true });

            dispatch(setTimelineStart(`${newDate}${timeLineStartValue}`));
        }
    };

    const updateRequestStartTime = (datepickerTime: DateTime) => {
        if (selectedDate) {
            const newDate = selectedDate.toISODate();

            const timelineStartDateTime = DateTime.fromISO(timelineStart);
            const diff = requestStartTime.diff(timelineStartDateTime);

            if (datepickerTime) {
                const newDateTime = DateTime.fromISO(
                    `${newDate}${datepickerTime.toISOTime({ includeOffset: true, includePrefix: true })}`,
                );

                if (
                    newDateTime > timelineStartDateTime &&
                    newDateTime.plus({ seconds: requestDuration }) < timelineStartDateTime.plus({ hours: 1 })
                ) {
                    dispatch(setRequestStartTime(newDateTime.toISO()));
                } else {
                    const timeLineStartValue = datepickerTime
                        .minus(diff)
                        .toISOTime({ includeOffset: true, includePrefix: true });
                    const difference = requestStartTime.toSeconds() - timelineStartDateTime.toSeconds();

                    if (datepickerTime.hour === 0 && datepickerTime.minute < difference / 60) {
                        // converting seconds to minutes via /60
                        //This if clause is to accomodate setting to midnight "=== 0" and factor in the offset value to allow appropriate clamping behavior
                        dispatch(
                            setTimelineStart(
                                DateTime.fromISO(`${newDate}${timeLineStartValue}`).minus({ days: 1 }).toISO(),
                            ),
                        );
                    } else {
                        dispatch(setTimelineStart(`${newDate}${timeLineStartValue}`));
                    }
                }
            }
        }
    };

    const handleDateChange = (date: MaterialUiPickersDate) => {
        if (date && unitMetadata.recordingStart) {
            const recordingStart = DateTime.fromISO(unitMetadata.recordingStart);

            if (!date.isValid || (date > recordingStart && date < DateTime.now())) {
                if (date.isValid) {
                    updateRequestStartDate(date);
                }

                setSelectedDate(date);
            }
        }
    };

    const handleTimeChange = (time: MaterialUiPickersDate) => {
        if (time && unitMetadata.recordingStart) {
            const recordingStart = DateTime.fromISO(unitMetadata.recordingStart).toSeconds();

            // Keep the local time and change timezone only for keyboard inputs
            // Keyboard inputs give time in local timezone
            if (time.zoneName !== unitMetadata.timezone) {
                time = time.setZone(unitMetadata.timezone, { keepLocalTime: true });
            }

            const newDatetimeISO = `${selectedDate?.toISODate()}${time.toISOTime({
                includeOffset: true,
                includePrefix: true,
            })}`;
            //From ISO returns time in browser timezone by default. So set the timezone of input time.
            const newDateTime = DateTime.fromISO(newDatetimeISO, { setZone: true });

            if (!newDateTime.isValid || (newDateTime.toSeconds() > recordingStart && newDateTime < DateTime.now())) {
                if (newDateTime.isValid) {
                    updateRequestStartTime(newDateTime);
                }

                setSelectedTime(newDateTime);
            }
        }
    };

    const handleDurationChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (Number(event.target.value) >= maxDurationInMinutes) {
            dispatch(setRequestDuration(maxDurationInMinutes * 60)); // converting from minutes to seconds (request duration is in seconds)
        } else {
            dispatch(setRequestDuration(Number(event.target.value) * 60)); // converting from minutes to seconds (request duration is in seconds)
        }
    };

    return (
        <div className={headerClasses.drawer} style={{ position: "absolute", zIndex: 2, width: "100%" }}>
            <div className={classes.headerContent}>
                <div className={classes.alignment} style={{ width: gridAndAccordionWidth }}>
                    <CloseIcon className={headerClasses.icon} onClick={handleVideoSelectionClose} fontSize="large" />

                    <div className={classes.selectionsContainer} style={{ width: timelineWidth }}>
                        <div className={classes.center}>
                            <MuiPickersUtilsProvider utils={LuxonAdapter}>
                                <ThemeProvider theme={pickerStyles}>
                                    <KeyboardDatePicker
                                        className={headerClasses.pickerBackground}
                                        label="Date"
                                        variant="inline"
                                        inputVariant="filled"
                                        format="MM/dd/yyyy"
                                        value={selectedDate?.setZone(unitMetadata.timezone)} // TODO: sub with other zone method once not using Date.now
                                        disableFuture={true}
                                        minDate={unitMetadata.recordingStart}
                                        onChange={handleDateChange}
                                        onAccept={(date) => {
                                            if (date) updateRequestStartDate(date);
                                        }}
                                        onBlur={() => {
                                            if (!selectedDate?.isValid) setSelectedDate(requestStartTime);
                                        }}
                                        style={{ minWidth: "150px" }}
                                    />
                                    <KeyboardTimePicker
                                        className={headerClasses.pickerBackground}
                                        label="Start Time"
                                        inputVariant="filled"
                                        variant="inline"
                                        mask="__:__ _M"
                                        format={"hh:mm a"}
                                        value={selectedTime?.setZone(unitMetadata.timezone)}
                                        placeholder="hh:mm A/P"
                                        keyboardIcon={<AccessTimeIcon />}
                                        onChange={handleTimeChange}
                                        onAccept={(time) => {
                                            if (time && time.isValid) updateRequestStartTime(time);
                                        }}
                                        onBlur={() => {
                                            if (!selectedTime?.isValid) setSelectedTime(requestStartTime);
                                        }}
                                        style={{ minWidth: "150px" }}
                                    />

                                    <TextField
                                        className={headerClasses.pickerBackground}
                                        required
                                        label="Duration (Min)"
                                        value={Math.round(requestDuration / 60)} //Divide by 60 to get to minutes
                                        onChange={handleDurationChange}
                                        placeholder="0 min"
                                        variant="filled"
                                        type="number"
                                        InputProps={{
                                            inputProps: { min: 0, max: 30 },
                                        }}
                                        fullWidth={false}
                                        style={{ width: "11%", minWidth: "120px" }}
                                    />
                                </ThemeProvider>
                            </MuiPickersUtilsProvider>
                            <div className={classes.endTimeContainer}>
                                <p>End</p>
                                <div className={classes.durationCalcWrapper}>
                                    {requestStartTime
                                        ?.plus({ seconds: requestDuration })
                                        .toLocaleString(DateTime.TIME_SIMPLE)}
                                    {" • "}
                                    {requestStartTime?.toLocaleString()}
                                </div>
                            </div>
                        </div>

                        <Button className={headerClasses.button} variant="contained" onClick={handleClickListItem}>
                            Request Video
                        </Button>
                    </div>
                </div>
                {modalOpen && (
                    <VideoRequestModal
                        classes={{
                            paper: classes.paper,
                        }}
                        id="camera-menu"
                        onClose={handleClose}
                    />
                )}
            </div>
        </div>
    );
};
